import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Loader2, User as UserIcon, Mail, Phone } from "lucide-react";

const formatErr = (detail) => {
  if (detail == null) return "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
};

export default function GuestDialog() {
  const { authOpen, setAuthOpen, loginWithToken } = useApp();
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setName(""); setMobile(""); setEmail(""); };

  const submit = async () => {
    if (!name.trim() || !mobile.trim() || !email.trim()) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/guest", { name: name.trim(), mobile: mobile.trim(), email: email.trim() });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome, ${data.user.name || "friend"}`);
      setAuthOpen(false);
      reset();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || "Sign-in failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={authOpen} onOpenChange={(v) => { setAuthOpen(v); if (!v) reset(); }}>
      <DialogContent data-testid="guest-dialog" className="bg-zinc-950 border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Enter your details to continue</DialogTitle>
          <DialogDescription className="text-white/60">
            We'll use this to contact you about your order.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
          <Button
            data-testid="google-signin-btn"
            type="button"
            onClick={() => {
              const redirectUrl = window.location.origin + "/auth/callback";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full bg-white text-zinc-900 hover:bg-zinc-100 font-medium"
          >
            <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <div className="relative flex items-center gap-3 py-1">
            <div className="flex-1 h-px bg-white/10"/>
            <span className="mono-label text-white/40 text-[10px]">or fill in your details</span>
            <div className="flex-1 h-px bg-white/10"/>
          </div>

          <div>
            <Label className="mono-label text-white/60 flex items-center gap-1.5"><UserIcon className="h-3.5 w-3.5"/> Full name</Label>
            <Input
              data-testid="guest-name-input"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="bg-zinc-900 border-white/10 focus-visible:ring-blue-600 mt-1.5"
            />
          </div>
          <div>
            <Label className="mono-label text-white/60 flex items-center gap-1.5"><Phone className="h-3.5 w-3.5"/> Mobile number</Label>
            <Input
              data-testid="guest-mobile-input"
              type="tel"
              placeholder="+91 98765 43210"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              className="bg-zinc-900 border-white/10 focus-visible:ring-blue-600 mt-1.5"
            />
          </div>
          <div>
            <Label className="mono-label text-white/60 flex items-center gap-1.5"><Mail className="h-3.5 w-3.5"/> Email</Label>
            <Input
              data-testid="guest-email-input"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="bg-zinc-900 border-white/10 focus-visible:ring-blue-600 mt-1.5"
            />
          </div>

          <Button
            data-testid="guest-submit-btn"
            onClick={submit}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white"
          >
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin"/>}
            Continue
          </Button>

          <p className="text-[10px] text-white/40 font-mono text-center">
            By continuing you agree to be contacted about your order.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
